import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  LockOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
  TeamOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Col,
  Empty,
  Flex,
  Input,
  Row,
  Spin,
  Tag,
  Tree,
  Typography,
} from 'antd';
import type { DataNode } from 'antd/es/tree';

import IBTooltip from '@totem/components/common/helpTooltips/IBTooltip';
import CategorizationNodeEditor from '@totem/components/ticketing/categorizationManagement/CategorizationNodeEditor';
import {
  findNode,
  MAX_CATEGORIZATION_LEVEL,
  nodeMatches,
} from '@totem/components/ticketing/categorizationManagement/categorizationUtilities';
import TicketCategorizationContext from '@totem/components/ticketing/categorizationManagement/TicketCategorizationContext';
import {
  CategorizationLevel,
  EditableCategorizationNode,
} from '@totem/components/ticketing/categorizationManagement/types';
import RecordCountBadge from '@totem/components/ui_components/widgets/RecordCountBadge';
import { formatDate } from '@totem/utilities/common';

const nodeTitle = (node: EditableCategorizationNode) => {
  const requirements = node.createRequirements;
  return (
    <Flex gap={6} align="center" wrap>
      <span>{node.name.trim() === '' ? <i>(no name)</i> : node.name}</span>
      {node.originalName === null && <Tag color="processing">New</Tag>}
      {requirements.internalOnly && (
        <IBTooltip content="Internal only">
          <LockOutlined />
        </IBTooltip>
      )}
      {requirements.organizations.length > 0 && (
        <IBTooltip
          content={`Limited to ${requirements.organizations.length} organization${requirements.organizations.length === 1 ? '' : 's'}`}
        >
          <Tag icon={<TeamOutlined />}>{requirements.organizations.length}</Tag>
        </IBTooltip>
      )}
    </Flex>
  );
};

const buildTreeData = (
  nodes: EditableCategorizationNode[],
  search: string,
  level: CategorizationLevel = 1,
): DataNode[] =>
  nodes
    .filter((node) => search === '' || nodeMatches(node, search))
    .map((node) => {
      // When a node's own name matches, show all of its children.
      const selfMatches = node.name.toLowerCase().includes(search);
      return {
        key: node.key,
        title: nodeTitle(node),
        isLeaf: level === MAX_CATEGORIZATION_LEVEL,
        children:
          level < MAX_CATEGORIZATION_LEVEL
            ? buildTreeData(
                node.children,
                selfMatches ? '' : search,
                (level + 1) as CategorizationLevel,
              )
            : undefined,
      };
    });

const collectKeys = (data: DataNode[]): string[] =>
  data.flatMap((item) => [
    item.key as string,
    ...collectKeys(item.children ?? []),
  ]);

export const TicketCategorizationExtra = () => {
  const {
    configuration,
    nodes,
    isDirty,
    saving,
    loading,
    validationErrors,
    onSave,
    onDiscard,
    onRefresh,
  } = useContext(TicketCategorizationContext);

  const saveButton = (
    <Button
      type="primary"
      icon={<SaveOutlined />}
      loading={saving}
      disabled={!isDirty || validationErrors.length > 0}
      onClick={onSave}
    >
      Save
    </Button>
  );

  return (
    <Flex gap="small" align="center" wrap>
      {configuration?.updatedAt && (
        <Typography.Text type="secondary">
          Last saved {formatDate(configuration.updatedAt)}
          {configuration.updatedByName
            ? ` by ${configuration.updatedByName}`
            : ''}
        </Typography.Text>
      )}
      <RecordCountBadge count={nodes.length} />
      {isDirty && <Tag color="warning">Unsaved changes</Tag>}
      <IBTooltip content="Reload">
        <Button
          icon={<ReloadOutlined />}
          shape="circle"
          disabled={loading || saving}
          onClick={onRefresh}
        />
      </IBTooltip>
      <Button
        icon={<UndoOutlined />}
        disabled={!isDirty || saving}
        onClick={onDiscard}
      >
        Discard
      </Button>
      {isDirty && validationErrors.length > 0 ? (
        <IBTooltip content={validationErrors[0].message}>
          {saveButton}
        </IBTooltip>
      ) : (
        saveButton
      )}
    </Flex>
  );
};

const TicketCategorizationLayout = () => {
  const {
    loading,
    configuration,
    nodes,
    selectedKey,
    validationErrors,
    setSelectedKey,
    onAddNode,
  } = useContext(TicketCategorizationContext);
  const [search, setSearch] = useState<string>('');
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);

  const normalizedSearch = search.trim().toLowerCase();
  const treeData = useMemo(
    () => buildTreeData(nodes, normalizedSearch),
    [nodes, normalizedSearch],
  );

  // Expand everything while searching.
  useEffect(() => {
    if (normalizedSearch !== '') {
      setExpandedKeys(collectKeys(treeData));
    }
  }, [normalizedSearch, treeData]);

  // Make sure the selected node is visible (e.g. after adding a child).
  useEffect(() => {
    if (selectedKey === null) {
      return;
    }
    const location = findNode(nodes, selectedKey);
    if (location === null) {
      return;
    }
    const ancestorKeys = location.ancestors.map((ancestor) => ancestor.key);
    setExpandedKeys((current) => {
      const missing = ancestorKeys.filter((key) => !current.includes(key));
      return missing.length > 0 ? [...current, ...missing] : current;
    });
  }, [selectedKey, nodes]);

  if (loading && configuration === null) {
    return (
      <Flex justify="center" style={{ padding: 48 }}>
        <Spin />
      </Flex>
    );
  }

  if (configuration === null) {
    return <Empty description="Ticket categorization could not be loaded." />;
  }

  const otherErrors = validationErrors.filter(
    (err) => err.key === '' || err.key !== selectedKey,
  );

  return (
    <Spin spinning={loading}>
      <Typography.Paragraph type="secondary">
        Ticket Types, Categories, and Sub-Categories available when creating a
        ticket. Restrictions on a parent also apply to everything under it.
      </Typography.Paragraph>
      {otherErrors.length > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          title="Fix these issues before saving"
          description={otherErrors.map((err) => (
            <div key={`${err.key}-${err.message}`}>{err.message}</div>
          ))}
        />
      )}
      <Row gutter={[24, 16]}>
        <Col xs={24} lg={10}>
          <Flex gap="small" style={{ marginBottom: 12 }}>
            <Input.Search
              allowClear
              placeholder="Search"
              value={search}
              onChange={(evt) => setSearch(evt.target.value)}
            />
            <Button icon={<PlusOutlined />} onClick={() => onAddNode(null)}>
              Add Ticket Type
            </Button>
          </Flex>
          {treeData.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                normalizedSearch !== '' ? 'No matches' : 'No ticket types'
              }
            />
          ) : (
            <Tree
              blockNode
              showLine
              treeData={treeData}
              expandedKeys={expandedKeys}
              onExpand={(keys) => setExpandedKeys(keys)}
              selectedKeys={selectedKey !== null ? [selectedKey] : []}
              onSelect={(keys) =>
                setSelectedKey(keys.length > 0 ? (keys[0] as string) : null)
              }
            />
          )}
        </Col>
        <Col xs={24} lg={14}>
          <CategorizationNodeEditor />
        </Col>
      </Row>
    </Spin>
  );
};

export default TicketCategorizationLayout;
